import React from 'react';
import { useTranslation } from 'react-i18next';

import { GlowAngleProperty, Root, Veil, Blobs, Ring, Caption, SrOnly } from './GeneratingWash.styles';

export type GeneratingWashProps = {
  /**
   * `field` (default) covers one control: a lit rim and a light wash, sized so
   * an input still reads as an input. `page` covers the whole viewport while a
   * WHOLE article is written, with no rim (there is no edge to trace) and a line
   * saying what is happening.
   */
  variant?: 'field' | 'page';
  /** Only used by `page`: what is being written, in the author's words. */
  caption?: string;
  /** Announced to assistive tech, which sees no animation at all. */
  label: string;
};

/**
 * "Lumio is writing this, here."
 *
 * One overlay, used by every Generate button in the editor: over the whole page
 * for a whole article, over the body editor, over each SEO field, over a list
 * row being filled in, over the hero thumbnail. Putting the wait ON the thing
 * being written is the point — a spinner in a corner tells an author that
 * something is happening, this tells them what.
 *
 * Drop it inside a host that is `position: relative` and establishes a stacking
 * context. It is `aria-hidden` and announces itself through a live region
 * instead: the animation carries no information a screen reader can use, and the
 * status is the same sentence in every case.
 */
export const GeneratingWash = ({ variant = 'field', caption, label }: GeneratingWashProps): React.ReactElement => {
  const { t } = useTranslation();
  const page = variant === 'page';

  return (
    <>
      <GlowAngleProperty />
      <Root $page={page} aria-hidden="true">
        <Veil $page={page} />
        {/* A page-wide wash needs a much larger blur to read as fog rather than
            as three coloured circles; a field-sized one would look like a bug. */}
        <Blobs $blur={page ? 90 : 26} $opacity={page ? 0.6 : 0.45} />
        {page ? null : <Ring />}
        {page && caption ? <Caption text={caption} tag="span" /> : null}
      </Root>
      <SrOnly role="status" aria-live="polite">
        {t(label)}
      </SrOnly>
    </>
  );
};

export default GeneratingWash;
